import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { ApiError, api } from './lib/api';
import { NotAuthorized } from './pages/NotAuthorized';
import { Dashboard } from './pages/Dashboard';
import { NewRun } from './pages/NewRun';
import { ProfileDetail } from './pages/ProfileDetail';
import { Profiles } from './pages/Profiles';
import { RunDetail } from './pages/RunDetail';
import { Runs } from './pages/Runs';
import { Settings } from './pages/Settings';
import { SignIn } from './pages/SignIn';

/**
 * Renders the signed-in app or bounces to sign-in.
 *
 * This is a rendering decision, not a security boundary — the API rejects an
 * absent or unauthorized token on every route regardless. SPEC-0003 §3.
 */
function Protected({ children }: { children: React.ReactNode }) {
	const { user, loading } = useAuth();
	const location = useLocation();

	// Authenticated is not the same as authorized. Ask the server once, in one
	// place, rather than letting each page discover it from whichever data call
	// happened to 403 first.
	const [authorized, setAuthorized] = useState<boolean | null>(null);

	useEffect(() => {
		if (user === null) {
			setAuthorized(null);
			return;
		}
		let cancelled = false;
		api.me().then(
			() => {
				if (!cancelled) setAuthorized(true);
			},
			(cause: unknown) => {
				if (cancelled) return;
				// 403 is a definitive answer. Anything else is a transient failure,
				// and locking someone out because the network blipped would be worse
				// than letting the page surface its own error.
				setAuthorized(!(cause instanceof ApiError && cause.isForbidden));
			},
		);
		return () => {
			cancelled = true;
		};
	}, [user]);

	if (loading || (user !== null && authorized === null)) {
		return (
			<main className="flex min-h-screen items-center justify-center">
				<span className="loading loading-spinner" />
			</main>
		);
	}

	if (user === null) {
		return <Navigate to="/sign-in" state={{ from: location.pathname }} replace />;
	}

	if (authorized === false) {
		return <NotAuthorized />;
	}

	return <>{children}</>;
}

export function App() {
	return (
		<BrowserRouter>
			<AuthProvider>
				<Routes>
					<Route path="/sign-in" element={<SignIn />} />
					<Route
						path="/"
						element={
							<Protected>
								<Dashboard />
							</Protected>
						}
					/>
					<Route
						path="/runs"
						element={
							<Protected>
								<Runs />
							</Protected>
						}
					/>
					<Route
						path="/runs/new"
						element={
							<Protected>
								<NewRun />
							</Protected>
						}
					/>
					<Route
						path="/runs/:id"
						element={
							<Protected>
								<RunDetail />
							</Protected>
						}
					/>
					<Route
						path="/profiles"
						element={
							<Protected>
								<Profiles />
							</Protected>
						}
					/>
					<Route
						path="/profiles/:id"
						element={
							<Protected>
								<ProfileDetail />
							</Protected>
						}
					/>
					<Route
						path="/settings"
						element={
							<Protected>
								<Settings />
							</Protected>
						}
					/>
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</AuthProvider>
		</BrowserRouter>
	);
}
